import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Accordion from 'react-bootstrap/Accordion';
import Form from 'react-bootstrap/Form';
import './ReportDetails.css';
import PageLayout from '../PageLayout/PageLayout';
import StatusBadge from '../StatusBadge/StatusBadge';
import ProcessingStepper from '../ProcessingStepper/ProcessingStepper';
import { useReportDetails, useReportStatus, useUpdateReportItemEdit, useBulkUpdateReportItemsExcluded } from '../../hooks/useReports';
import { CategorizedItem, CostRange, NegotiationStrategy, ReportFile, VALID_SEVERITIES } from '../../services/reportsApi';
import { CACHE_TIME } from '../../constants/cache';
import { COST_EDIT } from '../../constants/cost';
import { formatDate, formatCurrency, formatCostRange, formatRangeBand, telHref } from '../../utils/formatters';
import { groupItemsByCategory, calculateSeverityStats, itemCostRange, sumCostRanges } from '../../utils/categoryUtils';
import { getScoreLevel } from '../../utils/scoreUtils';
import { getItemBadgeType, getBadgeClassName, getAvailableBadgeTypes, BADGE_TYPE_ORDER, BadgeType } from '../../utils/badgeUtils';
import { useToast } from '../../contexts/ToastContext';
import { parseError } from '../../utils/errorHandlers';
import ShareReportModal, { ShareReviewItem } from '../ShareReportModal/ShareReportModal';
import { ReportDetailsSkeleton } from '../Skeleton/Skeleton';
import { EmptyState, EmptyStateAction } from '../EmptyState/EmptyState';

// A cost input's raw text as whole dollars. null for an empty field, text that
// is not a number, or a negative (the input's min is 0). The caller checks the
// upper bound, since an over-limit value gets its own message.
const parseCostInput = (raw: string): number | null => {
  if (raw.trim() === '') return null;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value);
};

const COST_OUT_OF_RANGE_MESSAGE =
  `Enter a cost between $0 and ${formatCurrency(COST_EDIT.MAX_ITEM_COST)} per item. Your change wasn't saved.`;

// An item with its live category and severity edits applied, the way the share
// email applies them. Returns the item itself when neither is edited. Cost edits
// are not applied here: the totals take them separately via itemCostRange.
const applyLabelEdits = (
  item: CategorizedItem,
  categoryEdits: Record<string, string>,
  severityEdits: Record<string, string>,
): CategorizedItem => {
  const editedCategory = categoryEdits[item.itemId];
  const editedSeverity = severityEdits[item.itemId];
  if (editedCategory === undefined && editedSeverity === undefined) return item;
  return {
    ...item,
    category: editedCategory !== undefined ? editedCategory : item.category,
    severity: editedSeverity !== undefined ? editedSeverity : item.severity,
  };
};

const ReportDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Decode the report ID from URL
  const reportId = id ? decodeURIComponent(id) : undefined;

  // Fetch report details from API
  const { data: initialReport, isLoading, error, refetch, dataUpdatedAt } = useReportDetails(reportId);

  // Poll for status updates when report is still processing
  const isProcessing = initialReport?.status === 'processing';
  const { data: polledReport } = useReportStatus(reportId, isProcessing);

  // Use polled data if available and still processing, otherwise use initial data
  const report = (isProcessing && polledReport) ? polledReport : initialReport;

  // Mutation for persisting per-item edits (edited_cost, excluded).
  const { mutate: persistItemEdit } = useUpdateReportItemEdit();

  // Bulk mutation for Select-All-Visible / Deselect-All-Visible / Deselect-All.
  const { mutate: bulkUpdateExcluded, isPending: isBulkUpdating } =
    useBulkUpdateReportItemsExcluded();

  // Arrived from UploadForm: show the submitted banner. The flag is read once
  // into state and then cleared from the history entry, so a reload or Back to
  // this entry does not show the banner again. The replace navigation keeps
  // this component mounted, so the state (and the banner) survives it.
  const arrivedFromUpload = Boolean(location.state?.fromUpload);
  const [showEmailMessage] = useState(arrivedFromUpload);
  React.useEffect(() => {
    if (arrivedFromUpload) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [arrivedFromUpload, location.pathname, location.search, navigate]);

  // Per-file key currently being refreshed+downloaded.
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const { showError } = useToast();

  // Item type filter state
  type ItemTypeFilter = 'all' | 'defect' | 'limitation' | 'informational' | 'needs_further_inspection';
  const [itemTypeFilter, setItemTypeFilter] = useState<ItemTypeFilter>('defect');

  // Classification-badge filter + sort state (refines/reorders the item-type
  // filtered list; keyed off the derived badge, not itemType).
  const [badgeFilter, setBadgeFilter] = useState<'all' | BadgeType>('all');
  const [badgeSort, setBadgeSort] = useState<'default' | 'badge'>('default');

  // Share: item selection and cost editing. Hydrated from persisted edits
  // (ReportItem.editedCost / excluded) exactly once per reportId when the
  // report first reaches status='completed'. Subsequent user edits drive
  // both local state (optimistic) and the backend (via useUpdateReportItemEdit).
  //
  // The one-shot hydration ref is load-bearing: React Query refetches the
  // report detail on every mutation success (to invalidate the cache), and
  // re-hydrating on each refetch would overwrite an in-flight optimistic
  // edit with stale server state.
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [costEdits, setCostEdits] = useState<Record<string, number>>({});
  const [categoryEdits, setCategoryEdits] = useState<Record<string, string>>({});
  const [severityEdits, setSeverityEdits] = useState<Record<string, string>>({});
  const [showShareModal, setShowShareModal] = useState(false);
  const hydratedReportIdRef = React.useRef<string | null>(null);
  // Items the user has typed into since their last commit, each mapped to its
  // costEdits value before the first keystroke (undefined = no override). costEdits
  // also holds hydrated saved overrides, so it can't tell a fresh keystroke from a
  // saved value; blur commits only dirty items, so clicking in and out never
  // creates an override, and the mapped value is what a cleared or rejected
  // field reverts to.
  const dirtyCostIds = React.useRef<Map<string, number | undefined>>(new Map());
  // The raw text of a cost input being edited, shown verbatim (an empty field
  // stays empty) until blur. costEdits carries only its valid, in-range value.
  const [costDrafts, setCostDrafts] = useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!report?.items || report.status !== 'completed' || !reportId) return;
    if (hydratedReportIdRef.current === reportId) return;
    hydratedReportIdRef.current = reportId;
    const hydratedSelection = new Set<string>();
    const hydratedCosts: Record<string, number> = {};
    const hydratedCategories: Record<string, string> = {};
    const hydratedSeverities: Record<string, string> = {};
    for (const i of report.items) {
      // excluded === false means the user previously opted this item into the
      // share; mirror the backend email predicate exactly (missing/true stay
      // deselected). Strict `=== false` so an undefined flag is never selected.
      if (i.excluded === false) hydratedSelection.add(i.itemId);
      if (i.editedCost !== null && i.editedCost !== undefined) hydratedCosts[i.itemId] = i.editedCost;
      if (i.editedCategory !== null && i.editedCategory !== undefined) hydratedCategories[i.itemId] = i.editedCategory;
      if (i.editedSeverity !== null && i.editedSeverity !== undefined) hydratedSeverities[i.itemId] = i.editedSeverity;
    }
    setSelectedItemIds(hydratedSelection);
    setCostEdits(hydratedCosts);
    setCategoryEdits(hydratedCategories);
    setSeverityEdits(hydratedSeverities);
  }, [report?.items, report?.status, reportId]);

  const downloadFiles = report?.files ?? [];

  /**
   * Trigger a download via a temporary anchor click.
   * The presigned URL already has `attachment` content-disposition; the
   * `download` attribute is a defensive backup so the browser always saves
   * rather than navigating away.
   */
  const downloadOneFromUrl = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /**
   * Download a single file, refreshing its presigned URL first when the cached
   * report data is older than URL_REFRESH_THRESHOLD_MS. The backend re-mints a
   * fresh ~1h URL on every GET /reports/{id}, so refetching guarantees a valid
   * URL well before the TTL. The common (fresh) case skips the refetch and is
   * a single anchor click with no added latency.
   */
  const handleFileDownload = async (file: ReportFile) => {
    const age = dataUpdatedAt ? Date.now() - dataUpdatedAt : Infinity;
    if (age <= CACHE_TIME.URL_REFRESH_THRESHOLD_MS) {
      downloadOneFromUrl(file.downloadUrl, file.name);
      return;
    }

    setDownloadingKey(file.s3Key);
    try {
      const result = await refetch();
      // React Query's refetch() does NOT reject on a failed query by default
      // (throwOnError is false) — it RESOLVES with { data: undefined, error }.
      // So the retryable failure is detected here, not in catch.
      if (result.error || !result.data) {
        showError('Could not start the download. Please try again.');
        return;
      }
      const freshFile = result.data.files?.find((f) => f.s3Key === file.s3Key);
      if (!freshFile) {
        showError('This file is no longer available. Please refresh the page.');
        return;
      }
      downloadOneFromUrl(freshFile.downloadUrl, freshFile.name);
    } catch (err) {
      // Belt-and-suspenders: refetch() rarely throws (throwOnError defaults
      // false), but handle it so an unexpected throw still surfaces cleanly.
      console.error('Download refresh failed:', parseError(err).userMessage);
      showError('Could not start the download. Please try again.');
    } finally {
      setDownloadingKey(null);
    }
  };

  const getScoreBadge = (score: number) => {
    const level = getScoreLevel(score);
    const label = level.charAt(0).toUpperCase() + level.slice(1);
    return <span className={`score-badge score-${level}`}>{label} ({score}%)</span>;
  };

  const getSeverityClass = (severity: string) => {
    const s = severity.toLowerCase();
    if (s === 'high' || s === 'critical' || s === 'severe') return 'high';
    if (s === 'medium' || s === 'moderate') return 'medium';
    if (s === 'low' || s === 'minor') return 'low';
    return 'informational';
  };

  // Map any severity input (including the API serializer default 'General'
  // and legacy aliases) to a canonical VALID_SEVERITIES value, so the
  // severity <select> always binds to one of its four <option>s rather than
  // silently showing the first option while holding an unmatched value.
  const toCanonicalSeverity = (severity: string): string => {
    const s = (severity || '').toLowerCase();
    if (s === 'high' || s === 'critical' || s === 'severe') return 'High';
    if (s === 'medium' || s === 'moderate') return 'Medium';
    if (s === 'low' || s === 'minor') return 'Low';
    return 'Informational';
  };

  // Renders the "Needs Contractor Evaluation" badge in place of a dollar amount.
  // Used when the backend has routed this finding through the contractor-eval
  // pricing branch (no catalog lookup, no priced estimate).
  const getContractorEvalBadge = () => (
    <span className="item-type-badge contractor-eval">
      <i className="bi bi-exclamation-triangle me-1"></i>
      Needs Contractor Evaluation
    </span>
  );

  // Resolves the single per-finding classification badge. Order is deliberate:
  // the contractor-eval flag and the itemType non-defect types are authoritative,
  // and complianceType is trusted ONLY for a priced (i.e. compliance-reviewed)
  // defect. Older reports carry a default 'Observation' on every unreviewed
  // item (newer ones carry null; see backend compliance.py), so reading
  // complianceType unconditionally would mislabel non-defects and unpriced
  // defects. Always returns a badge so no finding is left badge-less.
  const getClassificationBadge = (item: CategorizedItem) => {
    // Badge label/type is resolved by the shared util so the rendered badge and
    // the filter/sort key (which also call getItemBadgeType) cannot drift.
    const badgeType = getItemBadgeType(item);
    if (badgeType === 'Needs Contractor Evaluation') {
      // Special-cased: this badge renders an icon, not just text.
      return getContractorEvalBadge();
    }
    return (
      <span className={`item-type-badge ${getBadgeClassName(badgeType)}`}>
        {badgeType}
      </span>
    );
  };

  // Maps backend pricing-signal taxonomy to Thano's superscript legend. no_match
  // (²) was dropped in task 09: those items show their fallback range like any other.
  // Replacement (³) was never reachable and was deleted (BE-SVC-09).
  // Items can carry both ¹ and ⁴ (a minimum-charge item with secondary unit
  // pricing attached). Missing pricingCategory renders nothing (defensive).
  const getPricingSuperscript = (item: CategorizedItem): string => {
    if (!item.pricingCategory) return '';
    let sup = '';
    if (item.pricingCategory === 'minimum') sup += '¹';
    if (item.unitPricingAvailable === true) sup += '⁴';
    return sup;
  };

  // Filter items by type
  const filteredItems = React.useMemo(() => {
    if (!report?.items) return [];
    if (itemTypeFilter === 'all') return report.items;
    return report.items.filter(item =>
      item.itemType === itemTypeFilter
    );
  }, [report?.items, itemTypeFilter]);

  // Classification badges present in the current item-type view — drives the
  // badge filter options. Derived (not hardcoded) so new backend
  // classifications surface automatically.
  const availableBadgeTypes = React.useMemo(
    () => getAvailableBadgeTypes(filteredItems),
    [filteredItems],
  );

  // If the chosen badge isn't present in the current item-type view, fall back
  // to 'all' (avoids a dangling filter showing an empty list). Derived, not
  // stored — no useEffect, no flash. The select binds to this so it visibly
  // snaps to "All Badges" when its prior value disappears.
  const effectiveBadgeFilter =
    badgeFilter !== 'all' && availableBadgeTypes.includes(badgeFilter) ? badgeFilter : 'all';

  // Apply the badge filter then (optionally) a stable badge-type sort on top of
  // the item-type filtered list.
  const displayedItems = React.useMemo(() => {
    let items = filteredItems;
    if (effectiveBadgeFilter !== 'all') {
      items = items.filter(i => getItemBadgeType(i) === effectiveBadgeFilter);
    }
    if (badgeSort === 'badge') {
      // Stable: decorate with original index, sort by badge order then index so
      // API order is preserved within a badge type. Never mutate filteredItems.
      items = items
        .map((item, idx) => ({ item, idx }))
        .sort((a, b) => {
          const d =
            BADGE_TYPE_ORDER.indexOf(getItemBadgeType(a.item)) -
            BADGE_TYPE_ORDER.indexOf(getItemBadgeType(b.item));
          return d !== 0 ? d : a.idx - b.idx;
        })
        .map(({ item }) => item);
    }
    return items;
  }, [filteredItems, effectiveBadgeFilter, badgeSort]);

  // Share selection & cost edit handlers.
  //
  // Cost edits save on blur (commitCostEdit). Every keystroke only updates
  // the local overlay, so the input stays responsive without hammering PATCH.
  //
  // Exclude toggles save immediately. On error, the optimistic state rolls
  // back so the UI matches the server.
  //
  // Toast copy goes through parseError(error).userMessage per the PII rule
  // (.claude/rules/pii-redaction.md) — never expose error.message directly.
  const toggleItemExcluded = (itemId: string, nextExcluded: boolean) => {
    if (!reportId) return;
    const wasSelected = selectedItemIds.has(itemId);
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (nextExcluded) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
    persistItemEdit(
      { reportId, itemId, edit: { excluded: nextExcluded } },
      {
        onError: (err) => {
          setSelectedItemIds(prev => {
            const next = new Set(prev);
            if (wasSelected) next.add(itemId);
            else next.delete(itemId);
            return next;
          });
          showError(`Failed to save inclusion: ${parseError(err).userMessage}`);
        },
      },
    );
  };

  const toggleItemSelection = (itemId: string) => {
    // If currently selected → next state is excluded.
    toggleItemExcluded(itemId, selectedItemIds.has(itemId));
  };

  // Apply excluded=<bool> to a list of item IDs via the bulk endpoint.
  // Optimistic local update with rollback on error and partial-success
  // reconciliation. The early `isBulkUpdating` return serializes rapid clicks:
  // a second bulk operation is silently ignored while the first is in flight,
  // which avoids a stale-closure rollback bug.
  const applyBulkExcluded = (itemIds: string[], nextExcluded: boolean) => {
    if (!reportId || itemIds.length === 0 || isBulkUpdating) return;
    const previousSelection = selectedItemIds;
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (nextExcluded) {
        itemIds.forEach(id => next.delete(id));
      } else {
        itemIds.forEach(id => next.add(id));
      }
      return next;
    });
    bulkUpdateExcluded(
      { reportId, payload: { itemIds, excluded: nextExcluded } },
      {
        onSuccess: (data) => {
          if (data.updatedCount < itemIds.length) {
            const updatedIds = new Set(data.items.map(i => i.itemId));
            setSelectedItemIds(prev => {
              const next = new Set(prev);
              itemIds.forEach(id => {
                if (!updatedIds.has(id)) {
                  if (previousSelection.has(id)) next.add(id);
                  else next.delete(id);
                }
              });
              return next;
            });
            showError(
              `Some items could not be updated (${itemIds.length - data.updatedCount} of ${itemIds.length} skipped).`,
            );
          }
        },
        onError: (err) => {
          setSelectedItemIds(previousSelection);
          showError(`Failed to update selection: ${parseError(err).userMessage}`);
        },
      },
    );
  };

  const selectAllVisible = () => {
    // displayedItems (not filteredItems) so the badge filter is respected — only
    // rows the user can actually see get selected.
    const idsToSelect = displayedItems
      .filter(item => !selectedItemIds.has(item.itemId))
      .map(item => item.itemId);
    applyBulkExcluded(idsToSelect, false);
  };

  const deselectAllVisible = () => {
    const idsToDeselect = displayedItems
      .filter(item => selectedItemIds.has(item.itemId))
      .map(item => item.itemId);
    applyBulkExcluded(idsToDeselect, true);
  };

  const deselectAll = () => {
    if (!report?.items) return;
    const idsToDeselect = report.items
      .filter(item => selectedItemIds.has(item.itemId))
      .map(item => item.itemId);
    applyBulkExcluded(idsToDeselect, true);
  };

  const handleCostEdit = (itemId: string, raw: string) => {
    // Update local overlay immediately (optimistic). No PATCH until blur.
    if (!dirtyCostIds.current.has(itemId)) dirtyCostIds.current.set(itemId, costEdits[itemId]);
    setCostDrafts(prev => ({ ...prev, [itemId]: raw }));
    // An empty, invalid or over-limit draft is no edit in the live totals, never $0.
    const parsed = parseCostInput(raw);
    setCostEdits(prev => {
      const next = { ...prev };
      if (parsed === null || parsed > COST_EDIT.MAX_ITEM_COST) delete next[itemId];
      else next[itemId] = parsed;
      return next;
    });
  };

  // Blur on a dirty cost input. An empty field means "back to the original":
  // a saved override is cleared through resetCostEdit, an unsaved edit is just
  // dropped. A negative or over-limit value is not saved and the field reverts.
  const finishCostEdit = (itemId: string, raw: string) => {
    const before = dirtyCostIds.current.get(itemId);
    setCostDrafts(prev => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    const revertLocal = () => {
      dirtyCostIds.current.delete(itemId);
      setCostEdits(prev => {
        const next = { ...prev };
        if (before === undefined) delete next[itemId];
        else next[itemId] = before;
        return next;
      });
    };
    if (raw.trim() === '') {
      if (before !== undefined) resetCostEdit(itemId, before);
      else revertLocal();
      return;
    }
    const parsed = parseCostInput(raw);
    if (parsed === null || parsed > COST_EDIT.MAX_ITEM_COST) {
      revertLocal();
      showError(COST_OUT_OF_RANGE_MESSAGE);
      return;
    }
    setCostEdits(prev => ({ ...prev, [itemId]: parsed }));
    commitCostEdit(itemId, parsed);
  };

  const commitCostEdit = (itemId: string, newCost: number) => {
    if (!reportId) return;
    dirtyCostIds.current.delete(itemId);
    // Roll back to the saved override, not to costEdits: by blur time costEdits
    // already holds the value being rejected.
    const saved = report?.items?.find(i => i.itemId === itemId)?.editedCost;
    persistItemEdit(
      { reportId, itemId, edit: { editedCost: newCost } },
      {
        onError: (err) => {
          setCostEdits(prev => {
            const next = { ...prev };
            if (saved === null || saved === undefined) delete next[itemId];
            else next[itemId] = saved;
            return next;
          });
          showError(`Failed to save cost: ${parseError(err).userMessage}`);
        },
      },
    );
  };

  // previousEdit is what a failed reset restores. A cleared field passes the
  // override it had before typing, since costEdits no longer holds it.
  const resetCostEdit = (itemId: string, previousEdit: number | undefined = costEdits[itemId]) => {
    if (!reportId) return;
    dirtyCostIds.current.delete(itemId);
    setCostEdits(prev => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    persistItemEdit(
      { reportId, itemId, edit: { editedCost: null } },
      {
        onError: (err) => {
          if (previousEdit !== undefined) {
            setCostEdits(prev => ({ ...prev, [itemId]: previousEdit }));
          }
          showError(`Failed to reset cost: ${parseError(err).userMessage}`);
        },
      },
    );
  };

  // Category/severity edits commit on <select> change (one event per
  // selection — no keystroke storm, so no separate blur-to-commit step like
  // cost). Optimistic local overlay + pessimistic PATCH with rollback on
  // error, mirroring the cost-edit path.
  const handleCategoryEdit = (itemId: string, newCategory: string) => {
    if (!reportId) return;
    const previousEdit = categoryEdits[itemId];
    setCategoryEdits(prev => ({ ...prev, [itemId]: newCategory }));
    persistItemEdit(
      { reportId, itemId, edit: { editedCategory: newCategory } },
      {
        onError: (err) => {
          setCategoryEdits(prev => {
            const next = { ...prev };
            if (previousEdit === undefined) delete next[itemId];
            else next[itemId] = previousEdit;
            return next;
          });
          showError(`Failed to save category: ${parseError(err).userMessage}`);
        },
      },
    );
  };

  const resetCategoryEdit = (itemId: string) => {
    if (!reportId) return;
    const previousEdit = categoryEdits[itemId];
    setCategoryEdits(prev => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    persistItemEdit(
      { reportId, itemId, edit: { editedCategory: null } },
      {
        onError: (err) => {
          if (previousEdit !== undefined) {
            setCategoryEdits(prev => ({ ...prev, [itemId]: previousEdit }));
          }
          showError(`Failed to reset category: ${parseError(err).userMessage}`);
        },
      },
    );
  };

  const handleSeverityEdit = (itemId: string, newSeverity: string) => {
    if (!reportId) return;
    const previousEdit = severityEdits[itemId];
    setSeverityEdits(prev => ({ ...prev, [itemId]: newSeverity }));
    persistItemEdit(
      { reportId, itemId, edit: { editedSeverity: newSeverity } },
      {
        onError: (err) => {
          setSeverityEdits(prev => {
            const next = { ...prev };
            if (previousEdit === undefined) delete next[itemId];
            else next[itemId] = previousEdit;
            return next;
          });
          showError(`Failed to save severity: ${parseError(err).userMessage}`);
        },
      },
    );
  };

  const resetSeverityEdit = (itemId: string) => {
    if (!reportId) return;
    const previousEdit = severityEdits[itemId];
    setSeverityEdits(prev => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    persistItemEdit(
      { reportId, itemId, edit: { editedSeverity: null } },
      {
        onError: (err) => {
          if (previousEdit !== undefined) {
            setSeverityEdits(prev => ({ ...prev, [itemId]: previousEdit }));
          }
          showError(`Failed to reset severity: ${parseError(err).userMessage}`);
        },
      },
    );
  };

  // Category dropdown option source: the de-duplicated union of categories
  // present on this report's items. No canonical category taxonomy exists
  // (categories come from LLM/Craftsman output), so we source from existing
  // values rather than inventing a list.
  const categoryOptions = React.useMemo<string[]>(() => {
    if (!report?.items) return [];
    return Array.from(new Set(report.items.map(i => i.category).filter(Boolean))).sort();
  }, [report?.items]);

  // Selection spans all items, not the item-type filter. itemCostRange applies
  // the one include rule (an edit counts, an unedited contractor-eval item doesn't).
  const calculateShareTotal = (): CostRange => {
    if (!report?.items) return { low: 0, expected: 0, high: 0 };
    return sumCostRanges(
      report.items.filter(item => selectedItemIds.has(item.itemId)),
      costEdits,
    );
  };

  // Selected findings passed to the share modal's review step. Each row's range
  // comes from the same itemCostRange call the total above sums, so the list and
  // the total beside it cannot disagree.
  const selectedReviewItems = React.useMemo<ShareReviewItem[]>(() => {
    if (!report?.items) return [];
    return report.items
      .filter(item => selectedItemIds.has(item.itemId))
      .map(item => {
        const edited = applyLabelEdits(item, categoryEdits, severityEdits);
        return {
          itemId: item.itemId,
          category: edited.category,
          severity: edited.severity,
          costRange: itemCostRange(item, costEdits),
        };
      });
  }, [report?.items, selectedItemIds, costEdits, categoryEdits, severityEdits]);

  // Count items by type for dropdown labels
  const itemCounts = React.useMemo(() => {
    if (!report?.items) return { all: 0, defect: 0, limitation: 0, informational: 0, needs_further_inspection: 0 };
    return {
      all: report.items.length,
      defect: report.items.filter(i => i.itemType === 'defect').length,
      limitation: report.items.filter(i => i.itemType === 'limitation').length,
      informational: report.items.filter(i => i.itemType === 'informational').length,
      needs_further_inspection: report.items.filter(i => i.itemType === 'needs_further_inspection').length,
    };
  }, [report?.items]);

  // Negotiation Strategies render gates.
  // hasStrategies: normal populated case.
  // isDegradedStrategies: the negotiation stage degraded (completionWarning set)
  //   on a completed report that HAS findings but persisted zero strategies —
  //   distinguished from the zero-items case, where an empty list is expected.
  const hasStrategies =
    !!report?.negotiationOptions && report.negotiationOptions.length > 0;
  const isDegradedStrategies =
    report?.status === 'completed' &&
    !!report.completionWarning &&
    (report.items?.length ?? 0) > 0 &&
    (report.negotiationOptions?.length ?? 0) === 0;

  // The filtered items with live category and severity edits applied, for the
  // totals only. Visibility, order and row labels stay on filteredItems and
  // displayedItems, so an edit never hides, moves or relabels a row.
  const editedFilteredItems = React.useMemo(
    () => filteredItems.map(item => applyLabelEdits(item, categoryEdits, severityEdits)),
    [filteredItems, categoryEdits, severityEdits],
  );

  // Group items by category for summary
  // Both follow every live share edit (cost, category, severity), so the page
  // shows the same numbers the share email will.
  const categorySummary = React.useMemo(() => {
    return groupItemsByCategory(editedFilteredItems, costEdits);
  }, [editedFilteredItems, costEdits]);

  // Computed summary stats from filtered items (replaces static report.summary,
  // whose range totals cover all items and ignore overrides)
  const computedSummary = React.useMemo(() => {
    return calculateSeverityStats(editedFilteredItems, costEdits);
  }, [editedFilteredItems, costEdits]);

  // Loading state
  if (isLoading) {
    return (
      <PageLayout title="Report Details" subtitle="Loading report...">
        <div className="report-details-loading report-details-loading--skeleton">
          <ReportDetailsSkeleton />
          <p className="visually-hidden">Loading report details...</p>
        </div>
      </PageLayout>
    );
  }

  // Error state
  if (error || !report) {
    const appError = error ? parseError(error) : null;
    const actions: EmptyStateAction[] = [];
    if (!appError || appError.retryable) actions.push({ label: 'Try Again', onClick: () => refetch() });
    actions.push({ label: 'Back to Reports', onClick: () => navigate('/reports'), variant: 'secondary' });
    return (
      <PageLayout title="Report Details" subtitle="Error loading report">
        <EmptyState
          icon="bi bi-exclamation-triangle"
          iconColor="red"
          title="Failed to load report"
          message={appError?.userMessage || 'Report not found'}
          actions={actions}
        />
      </PageLayout>
    );
  }

  // Processing state
  if (report.status === 'processing') {
    return (
      <PageLayout
        title="Report Details"
        subtitle="Your report is being processed"
      >
        <div className="report-processing">
          <div className="processing-card">
            <div className="spinner"></div>
            <h3>Processing Your Report</h3>
            <p>Our AI is analyzing your inspection report. This usually takes 3-5 minutes.</p>
            <ProcessingStepper currentStage={report.processingStage} />
            <div className="processing-info">
              <p className="hint">You can wait here or check back later from My Reports.</p>
              <button className="btn btn-outline-primary" onClick={() => navigate('/reports')}>
                View All Reports
              </button>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Pending state: the report row exists but the upload never reached
  // begin-processing, so there is nothing to show and nothing was charged (the
  // charge follows the pending -> processing flip). No polling here: only the
  // user's own begin-processing call moves a report out of pending.
  if (report.status === 'pending') {
    return (
      <PageLayout
        title="Report Details"
        subtitle="Upload incomplete"
      >
        <div className="report-processing">
          <div className="processing-card">
            <i className="bi bi-cloud-slash text-warning" style={{ fontSize: '2.5rem' }} aria-hidden="true"></i>
            <h3>Upload Incomplete</h3>
            <p>
              {"This report's upload didn't finish, so it was never processed and no run was charged. "}
              Upload the inspection report again to analyze it.
            </p>
            <div className="processing-info">
              <button className="btn btn-primary me-2" onClick={() => navigate('/upload')}>
                Upload Again
              </button>
              <button className="btn btn-outline-primary" onClick={() => navigate('/reports')}>
                Back to Reports
              </button>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Failed state
  if (report.status === 'failed') {
    return (
      <PageLayout
        title="Report Details"
        subtitle="Report processing failed"
      >
        <div className="report-processing">
          <div className="processing-card">
            <i className="bi bi-x-circle text-danger" style={{ fontSize: '2.5rem' }}></i>
            <h3>Unable to Process Report</h3>
            <p>{report.errorMessage || 'An error occurred while processing your inspection report. Please try uploading the file again.'}</p>
            <div className="processing-info">
              <button className="btn btn-primary me-2" onClick={() => navigate('/upload')}>
                Upload New Report
              </button>
              <button className="btn btn-outline-primary" onClick={() => navigate('/reports')}>
                View All Reports
              </button>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Build full address
  const fullAddress = [report.propertyAddress, report.city, report.state, report.zip]
    .filter(Boolean)
    .join(', ');

  // Single-defect cost threshold from the contract terms captured at upload.
  // Stored as a string (or absent); parseFloat('' | undefined) -> NaN, and
  // `NaN || 0` -> 0, so missing/blank/0 all mean "no threshold" (no badge).
  // `report` is guaranteed non-null here (past the loading/error/processing guards).
  const singleDefectThreshold =
    parseFloat(report.contractMetadata?.single_defect_threshold ?? '') || 0;

  return (
    <PageLayout
      title="Report Details"
      subtitle={showEmailMessage ? "Your report has been submitted for processing" : `Viewing report for ${report.propertyAddress?.split(',')[0] || 'Property'}`}
    >
      {showEmailMessage && (
        <div className="email-notification-card">
          <div className="notification-icon">
            <i className="bi bi-envelope-check"></i>
          </div>
          <div className="notification-content">
            <h3>Report Submitted Successfully!</h3>
            <p>
              Your property inspection report has been processed using our AI-powered analysis.
              The results are now available below.
            </p>
          </div>
        </div>
      )}

      {report.pricingWarning && (
        <div className="pricing-warning-card" role="status">
          <div className="warning-icon">
            <i className="bi bi-exclamation-triangle-fill"></i>
          </div>
          <div className="warning-content">
            <h3>Pricing Unavailable</h3>
            <p>{report.pricingWarning}</p>
          </div>
        </div>
      )}

      {report.completionWarning && (
        <div className="completion-warning-card" role="status">
          <div className="warning-icon">
            <i className="bi bi-exclamation-triangle-fill"></i>
          </div>
          <div className="warning-content">
            <h3>Report Completed with a Warning</h3>
            <p>
              Your report finished, but a non-essential final step didn&apos;t complete, so
              some negotiation-summary details may be missing. Your inspection findings
              and repair-cost estimates are unaffected. You can re-run the report or
              contact support if anything looks off.
            </p>
          </div>
        </div>
      )}

      {report.status === 'completed' && (report.items?.length ?? 0) === 0 && (
        <div className="no-findings-card" role="status">
          <div className="warning-icon">
            <i className="bi bi-info-circle-fill"></i>
          </div>
          <div className="warning-content">
            <h3>No Issues Identified</h3>
            <p>
              Our analysis didn&apos;t identify any defects in this document. If that
              matches your inspection, great news. If you expected findings, double-check
              that you uploaded the complete home inspection report (not a cover page,
              invoice, or disclosure form) and upload again if needed.
            </p>
          </div>
        </div>
      )}

      <div className="report-details-container">
        {/* Always-visible summary band — surfaces the verdict (score, cost,
            severity counts, address, date, status) without any clicks. Replaces
            the former Report Overview + Analysis Summary accordions and the
            Findings Only / All Items toggle. Counts reuse computedSummary, so
            they continue to reflect the current item-type filter. */}
        {report.status === 'completed' && (
          <div className="report-summary-band">
            <div className="summary-band-header">
              <div className="summary-band-meta">
                <div className="overview-item">
                  <span className="overview-label">Property Address</span>
                  <div className="property-address-display">{fullAddress || report.propertyAddress}</div>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Upload Date</span>
                  <div className="upload-date">{formatDate(report.uploadDate, { monthFormat: 'long' })}</div>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Status</span>
                  <div><StatusBadge status={report.status} /></div>
                </div>
              </div>
              {typeof report.aiScore === 'number' && (
                <div className="summary-band-score">{getScoreBadge(report.aiScore)}</div>
              )}
            </div>

            <div className="summary-stats">
              <div className="stat-item high">
                <div className="stat-value">{computedSummary.highCount}</div>
                <div className="stat-label">High Issues</div>
              </div>
              <div className="stat-item medium">
                <div className="stat-value">{computedSummary.mediumCount}</div>
                <div className="stat-label">Medium Issues</div>
              </div>
              <div className="stat-item low">
                <div className="stat-value">{computedSummary.lowCount}</div>
                <div className="stat-label">Low Issues</div>
              </div>
              <div className="stat-item total">
                <div className="stat-value">{computedSummary.totalItems}</div>
                <div className="stat-label">Total Items</div>
              </div>
            </div>

            {computedSummary.totalRepairCostRange.high > 0 && (
              <div className="total-cost-display">
                <i className="bi bi-cash me-2"></i>
                <span className="cost-label">Estimated Total Repair Cost:</span>
                <span className="cost-value">{formatCostRange(computedSummary.totalRepairCostRange)}</span>
                <p className="cost-framing">
                  Cost context for negotiation, not a contractor quote. Expected is
                  PropWise&apos;s estimate; the range allows for local variation.
                </p>
              </div>
            )}
          </div>
        )}
        <Accordion defaultActiveKey="inspectionFindings" className="report-accordion">
          {/* File Information */}
          <Accordion.Item eventKey="fileInformation">
            <Accordion.Header>
              <i className="bi bi-file-pdf section-icon"></i>
              <span className="section-title">
                File Information
                {downloadFiles.length > 0 && ` (${downloadFiles.length})`}
              </span>
            </Accordion.Header>
            <Accordion.Body>
              {downloadFiles.length > 0 ? (
                <ul className="file-info-list">
                  {downloadFiles.map((f) => (
                    <li key={f.s3Key} className="file-info-row">
                      <i className="bi bi-file-pdf file-icon"></i>
                      <span className="file-name">{f.name}</span>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm download-btn"
                        onClick={() => handleFileDownload(f)}
                        disabled={report.status !== 'completed' || downloadingKey === f.s3Key}
                        title={
                          report.status !== 'completed'
                            ? 'Report still processing'
                            : 'Download this PDF'
                        }
                      >
                        {downloadingKey === f.s3Key ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Downloading...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-download me-1"></i>
                            Download
                          </>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                /* No files listed (none found, or the listing failed): show
                   the name without a download. */
                <div className="file-info-grid">
                  <div className="file-info-item">
                    <i className="bi bi-file-pdf file-icon"></i>
                    <div className="file-details">
                      <div className="file-name">{report.fileName}</div>
                      <div className="file-metadata">
                        <span className="file-type">PDF Document</span>
                      </div>
                    </div>
                    <span className="text-muted small">Download not available</span>
                  </div>
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>

          {/* Contractor-verification disclaimer — summary-level caveat for Craftsman-priced
              estimates. Standalone block (not inside an accordion) so it stays visible above
              the Analysis Summary and Cost by Category sections. */}
          {report.status === 'completed' && report.items && report.items.length > 0 && (
            <div className="contractor-disclaimer">
              <h6 className="contractor-disclaimer-title">
                <i className="bi bi-info-circle me-2"></i>About these estimates
              </h6>
              <p>
                PropWise repair estimates are derived from licensed national construction
                cost data with local ZIP-code adjustments. Local contractor quotes will
                vary based on site conditions, access, and labor market. These figures are
                a reference baseline, not a substitute for a contractor quote.
              </p>
            </div>
          )}

          {/* Category Summary - Cost by Trade */}
          {categorySummary.length > 0 && (
            <Accordion.Item eventKey="costByCategory">
              <Accordion.Header>
                <i className="bi bi-pie-chart section-icon"></i>
                <span className="section-title">Cost by Category</span>
              </Accordion.Header>
              <Accordion.Body>
                <div className="category-summary-list">
                  {categorySummary.map((cat) => (
                    <div key={cat.category} className="category-summary-item">
                      <div className="category-info">
                        <span className="category-name">{cat.category}</span>
                        <span className="category-count">{cat.count} {cat.count === 1 ? 'item' : 'items'}</span>
                      </div>
                      <span className="category-cost">{formatCostRange(cat.costRange)}</span>
                    </div>
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          )}

          {/* Pricing notation legend (Thano's drafted wording, verbatim).
              Static block — no conditional rendering; the user sees the legend
              even when no superscripts are present so the report explains its
              own pricing taxonomy. */}
          {report.status === 'completed' && (
            <div className="pricing-legend">
              <h6 className="pricing-legend-title">Pricing notation</h6>
              <dl className="pricing-legend-list">
                <dt>¹ Minimum trade/service charge</dt>
                <dd>This estimate reflects a baseline charge for a contractor or trade professional to evaluate or complete a small-scope repair. It is not a fully scoped repair quote and may change based on contractor review, material needs, access, and actual repair scope.</dd>
                <dt>⁴ Scope-dependent unit pricing available</dt>
                <dd>Related LF/SF/unit pricing exists for this type of work, but the inspection report does not provide enough detail to calculate a full repair cost. The amount shown is a starting-point estimate. Unit pricing may be used if a contractor identifies the affected quantity.</dd>
              </dl>
            </div>
          )}

          {/* Inspection Findings - always renders for completed reports */}
          {report.status === 'completed' && (
            <Accordion.Item eventKey="inspectionFindings" className="findings-accordion-item">
              <div className="findings-header-wrapper">
                <Accordion.Header>
                  <i className="bi bi-robot section-icon"></i>
                  <span className="section-title">Inspection Findings</span>
                </Accordion.Header>
                {report.items && report.items.length > 0 && (
                  <div className="header-right">
                    <select
                      className="form-select form-select-sm filter-select"
                      value={itemTypeFilter}
                      onChange={(e) => setItemTypeFilter(e.target.value as ItemTypeFilter)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Filter findings by item type"
                    >
                      <option value="all">All Items ({itemCounts.all})</option>
                      <option value="defect">Findings ({itemCounts.defect})</option>
                      <option value="limitation">Limitations ({itemCounts.limitation})</option>
                      <option value="informational">Informational ({itemCounts.informational})</option>
                      <option value="needs_further_inspection">Needs Further Inspection ({itemCounts.needs_further_inspection})</option>
                    </select>
                    <select
                      className="form-select form-select-sm filter-select"
                      value={effectiveBadgeFilter}
                      onChange={(e) => setBadgeFilter(e.target.value as 'all' | BadgeType)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Filter by classification badge"
                    >
                      <option value="all">All Badges</option>
                      {availableBadgeTypes.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <select
                      className="form-select form-select-sm filter-select"
                      value={badgeSort}
                      onChange={(e) => setBadgeSort(e.target.value as 'default' | 'badge')}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Sort findings by badge type"
                    >
                      <option value="default">Default order</option>
                      <option value="badge">Sort by badge type</option>
                    </select>
                  </div>
                )}
              </div>
              <Accordion.Body>
                {report.items && report.items.length > 0 ? (
                  <>
                    {report.status === 'completed' && (
                      <div className="selection-controls">
                        <span className="selection-count">
                          {selectedItemIds.size} items selected for sharing
                        </span>
                        <button
                          className="btn btn-sm btn-link"
                          onClick={selectAllVisible}
                          disabled={isBulkUpdating}
                        >
                          Select All Visible
                        </button>
                        <button
                          className="btn btn-sm btn-link"
                          onClick={deselectAllVisible}
                          disabled={isBulkUpdating}
                        >
                          Deselect All Visible
                        </button>
                        <button
                          className="btn btn-sm btn-link"
                          onClick={deselectAll}
                          disabled={isBulkUpdating}
                          aria-label="Deselect everything across categories"
                        >
                          Deselect All
                        </button>
                      </div>
                    )}
                    <div className="issues-list">
                      {displayedItems.length === 0 && (
                        <EmptyState
                          size="inline"
                          icon="bi bi-search"
                          iconColor="muted"
                          message={`No ${itemTypeFilter === 'all' ? 'items' : itemTypeFilter === 'defect' ? 'findings' : itemTypeFilter + 's'} found.`}
                        />
                      )}
                      {displayedItems.map((item: CategorizedItem, rowIndex: number) => {
                        const costDraft = costDrafts[item.itemId];
                        // A draft counts, so clearing a contractor-eval override's field
                        // does not swap the input out from under the cursor.
                        const hasEdit = costEdits[item.itemId] !== undefined || costDraft !== undefined;
                        const hasCategoryEdit = categoryEdits[item.itemId] !== undefined;
                        const hasSeverityEdit = severityEdits[item.itemId] !== undefined;
                        // The number in the cost input and the value Over Threshold compares:
                        // the edit, else the whole-dollar expected value, else estimatedCost
                        // (pre-07 payload). The unedited contractor-eval branch below never
                        // reads it; ?? 0 is a defensive fallback.
                        const effectiveCost =
                          costEdits[item.itemId] ?? item.costRange?.expected ?? item.estimatedCost ?? 0;
                        const displayRange = itemCostRange(item, costEdits);
                        const band = item.costRange ? formatRangeBand(item.costRange) : null;
                        // Edited overrides win for select binding + row styling, so the
                        // severity stripe restyles immediately on edit.
                        const selectedCategory = categoryEdits[item.itemId] ?? item.category;
                        const selectedSeverity =
                          severityEdits[item.itemId] ?? toCanonicalSeverity(item.severity);
                        const superscript = getPricingSuperscript(item);
                        // Names this row's controls apart from other rows in the same
                        // category: the visible position in the list, 1-based.
                        const rowLabel = `${item.category} item ${rowIndex + 1}`;
                        return (
                        <div key={item.itemId} className={`issue-item ${getSeverityClass(selectedSeverity)}`}>
                          {report.status === 'completed' && (
                            <div className="issue-select">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedItemIds.has(item.itemId)}
                                onChange={() => toggleItemSelection(item.itemId)}
                                aria-label={`Include ${rowLabel} in shared report`}
                              />
                            </div>
                          )}
                          <div className="issue-item-content">
                            <div className="issue-header">
                              {report.status === 'completed' ? (
                                <span className="issue-severity-edit">
                                  <Form.Select
                                    size="sm"
                                    className={`severity-edit-select ${getSeverityClass(selectedSeverity)}`}
                                    value={selectedSeverity}
                                    onChange={(e) => handleSeverityEdit(item.itemId, e.target.value)}
                                    aria-label={`Edit severity for ${rowLabel}`}
                                  >
                                    {VALID_SEVERITIES.map(s => (
                                      <option key={s} value={s}>{s}</option>
                                    ))}
                                  </Form.Select>
                                  {hasSeverityEdit && (
                                    <button
                                      className="btn btn-link btn-sm severity-reset"
                                      onClick={() => resetSeverityEdit(item.itemId)}
                                      title="Reset to original"
                                      aria-label={`Reset severity for ${rowLabel} to original`}
                                    >
                                      <i className="bi bi-arrow-counterclockwise"></i>
                                    </button>
                                  )}
                                </span>
                              ) : (
                                <span className={`issue-severity ${getSeverityClass(item.severity)}`}>
                                  {item.severity}
                                </span>
                              )}
                              {getClassificationBadge(item)}
                              {report.status === 'completed' ? (
                                <span className="issue-category-edit">
                                  <Form.Select
                                    size="sm"
                                    className="category-edit-select"
                                    value={selectedCategory}
                                    onChange={(e) => handleCategoryEdit(item.itemId, e.target.value)}
                                    aria-label={`Edit category for ${rowLabel}`}
                                  >
                                    {/* Guarantee the item's own current category is selectable
                                        even if it isn't in the report-wide union (defensive). */}
                                    {(categoryOptions.includes(selectedCategory)
                                      ? categoryOptions
                                      : [selectedCategory, ...categoryOptions]
                                    ).map(c => (
                                      <option key={c} value={c}>{c}</option>
                                    ))}
                                  </Form.Select>
                                  {hasCategoryEdit && (
                                    <button
                                      className="btn btn-link btn-sm category-reset"
                                      onClick={() => resetCategoryEdit(item.itemId)}
                                      title="Reset to original"
                                      aria-label={`Reset category for ${rowLabel} to original`}
                                    >
                                      <i className="bi bi-arrow-counterclockwise"></i>
                                    </button>
                                  )}
                                </span>
                              ) : (
                                <span className="issue-category">{item.category}</span>
                              )}
                              {item.pageNumber && (
                                <span className="page-number">p. {item.pageNumber}</span>
                              )}
                              {item.complianceFlag && (
                                <span className="compliance-flag">
                                  <i className="bi bi-exclamation-circle me-1"></i>
                                  Non-Compliant
                                </span>
                              )}
                              {/* Cost-vs-threshold flag. Same rule as the backend's
                                  check_contract_thresholds (defects only, strict >,
                                  threshold > 0), but compares the whole-dollar expected
                                  value where the backend compares cents, so the two can
                                  differ by under $0.50 at the boundary. Distinct (amber)
                                  from the red compliance flag above. Keys off
                                  effectiveCost so user cost edits toggle it live. */}
                              {item.itemType === 'defect' &&
                                singleDefectThreshold > 0 &&
                                effectiveCost > singleDefectThreshold && (
                                <span className="threshold-flag">
                                  <i className="bi bi-exclamation-diamond me-1"></i>
                                  Over Threshold
                                </span>
                              )}
                            </div>
                            <div className="issue-description">{item.itemText}</div>
                            <div className="issue-footer">
                              {item.needsContractorEvaluation === true && !hasEdit ? (
                                <span className="issue-cost contractor-eval-note">Contractor quote</span>
                              ) : report.status === 'completed' && item.itemType !== 'informational' ? (
                                <span className="issue-cost editable">
                                  <i className="bi bi-cash me-1"></i>
                                  $<input
                                    type="number"
                                    className="cost-edit-input"
                                    value={costDraft ?? effectiveCost}
                                    onChange={(e) => handleCostEdit(item.itemId, e.target.value)}
                                    onBlur={(e) => {
                                      // Focus + blur without typing is not an override.
                                      if (!dirtyCostIds.current.has(item.itemId)) return;
                                      finishCostEdit(item.itemId, e.target.value);
                                    }}
                                    min="0"
                                    max={COST_EDIT.MAX_ITEM_COST}
                                    step="1"
                                    aria-label={`Edit cost for ${rowLabel}`}
                                  />
                                  {superscript && <sup className="pricing-superscript">{superscript}</sup>}
                                  {hasEdit ? (
                                    <span className="cost-original text-muted ms-2">
                                      {item.costRange
                                        ? `(original ${formatCurrency(item.costRange.expected)}${band ? `, ${band}` : ''})`
                                        : `(original ${formatCurrency(item.estimatedCost ?? 0)})`}
                                    </span>
                                  ) : band && (
                                    <span className="cost-original text-muted ms-2">({band})</span>
                                  )}
                                  {hasEdit && (
                                    <button
                                      className="btn btn-link btn-sm cost-reset"
                                      onClick={() => resetCostEdit(item.itemId)}
                                      title="Reset to original"
                                      aria-label={`Reset cost for ${rowLabel} to original`}
                                    >
                                      <i className="bi bi-arrow-counterclockwise"></i>
                                    </button>
                                  )}
                                </span>
                              ) : (
                                <span className="issue-cost">
                                  <i className="bi bi-cash me-1"></i>
                                  {displayRange ? formatCostRange(displayRange) : formatCurrency(effectiveCost)}
                                  {superscript && <sup className="pricing-superscript">{superscript}</sup>}
                                </span>
                              )}
                            </div>
                            {item.vendors && item.vendors.length > 0 && (
                              <ul className="issue-vendors">
                                {item.vendors.map((vendor, idx) => (
                                  <li key={`${item.itemId}-vendor-${idx}`} className="vendor-info">
                                    <i className="bi bi-building me-1"></i>
                                    <span className="vendor-name">{vendor.name}</span>
                                    {telHref(vendor.phone) && (
                                      <a className="vendor-link" href={telHref(vendor.phone)}>
                                        <i className="bi bi-telephone me-1"></i>{vendor.phone}
                                      </a>
                                    )}
                                    {vendor.website && (
                                      <a
                                        className="vendor-link"
                                        href={vendor.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <i className="bi bi-globe me-1"></i>Website
                                      </a>
                                    )}
                                    {vendor.google_profile && (
                                      <a
                                        className="vendor-link"
                                        href={vendor.google_profile}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <i className="bi bi-geo-alt me-1"></i>View on Google
                                      </a>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <EmptyState
                    size="inline"
                    icon="bi bi-check-circle"
                    iconColor="blue"
                    message="No inspection findings to display."
                  />
                )}
              </Accordion.Body>
            </Accordion.Item>
          )}

          {/* Negotiation Strategies */}
          {(hasStrategies || isDegradedStrategies) && (
            <Accordion.Item eventKey="negotiationStrategies">
              <Accordion.Header>
                <i className="bi bi-chat-left-quote section-icon"></i>
                <span className="section-title">Negotiation Strategies</span>
              </Accordion.Header>
              <Accordion.Body>
                {hasStrategies ? (
                  <div className="strategies-list">
                    {report.negotiationOptions?.map((strategy: NegotiationStrategy, index: number) => (
                      <div key={index} className="strategy-card">
                        <div className="strategy-number">{index + 1}</div>
                        <div className="strategy-content">
                          <div className="strategy-label">{strategy.label}</div>
                          <div className="strategy-summary">{strategy.summary}</div>
                          {strategy.recommended_ask && (
                            <div className="strategy-recommended-ask">
                              <strong>Recommended ask:</strong> {strategy.recommended_ask}
                            </div>
                          )}
                          {strategy.key_points.length > 0 && (
                            <ul className="strategy-key-points">
                              {strategy.key_points.map((point, i) => (
                                <li key={i}>{point}</li>
                              ))}
                            </ul>
                          )}
                          <span className={`strategy-risk risk-${strategy.risk_level}`}>
                            {strategy.risk_level} risk
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    size="inline"
                    icon="bi bi-chat-left-quote"
                    iconColor="muted"
                    message="Negotiation strategies couldn't be generated for this report. Review the flagged findings with your agent."
                  />
                )}
              </Accordion.Body>
            </Accordion.Item>
          )}
        </Accordion>

        {/* Sticky action bar — stays visible while scrolling the findings list,
            keeping the selection count and Share button near the checkboxes that
            drive them. Replaces the former bottom .action-buttons block. */}
        <div className="sticky-action-bar">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/reports')}
          >
            <i className="bi bi-arrow-left me-2" aria-hidden="true"></i>
            {/* Visually hidden at phone width (CSS), still the button's name. */}
            <span className="sticky-btn-label">Back to Reports</span>
          </button>

          {report.status === 'completed' && (
            <div className="sticky-action-bar-right">
              <span className="sticky-selection-count">{selectedItemIds.size} selected</span>
              <button
                className="btn btn-primary"
                onClick={() => {
                  // Force any in-flight cost-input edit to fire its blur handler
                  // before the modal mounts. Without this, a user who types in a
                  // cost field then clicks Share never triggers the PATCH and the
                  // in-flight save guard in the modal has nothing to wait on.
                  (document.activeElement as HTMLElement | null)?.blur();
                  setShowShareModal(true);
                }}
                disabled={selectedItemIds.size === 0}
              >
                <i className="bi bi-send me-2"></i>
                Share Report ({selectedItemIds.size})
              </button>
            </div>
          )}
        </div>
      </div>

      {showShareModal && report && (
        <ShareReportModal
          reportId={report.id}
          selectedItemCount={selectedItemIds.size}
          totalCostRange={calculateShareTotal()}
          selectedItems={selectedReviewItems}
          onClose={() => setShowShareModal(false)}
          onSuccess={() => setShowShareModal(false)}
        />
      )}
    </PageLayout>
  );
};

export default ReportDetails;
